import {ItemType, PurchaseItem} from "@/store/shop/types";
import CU from "../utils/c-u";
import Sprite = Phaser.GameObjects.Sprite;
import Text = Phaser.GameObjects.Text;
import SpriteUtils from "@/utils/sprite-utils";
import {Keys} from "@/constants/key";
import AtlasUtils from "@/utils/atlas-utils";
import TextUtils from "@/utils/text-utils";
import Container = Phaser.GameObjects.Container;
import CreaturePanel from "@/scenes.ui/creature-panel";
import {Fonts} from "@/constants/font";
import {CreatureUtils} from "@/utils/creature-utils";
import {SpaceTr, Tr} from "@/i18n/i18n";
import OnceCaller from "@/utils/once-caller";
import Image = Phaser.GameObjects.Image;
import SContainer from "@/cmp/s-container";

export class PurchaseItemPanel extends SContainer {

    private heroDescCallback!: (heroId: string) => void;

    constructor(scene: Phaser.Scene, x: number, y: number, decorationId: string, pi: PurchaseItem,
                heroDescCallback: (heroId: string) => void) {
        super(scene, x, y);

        this.width = CU.CANVAS_WIDTH;
        this.heroDescCallback = heroDescCallback;

        let stand = this.createStandSprite(decorationId);
        let main = this.createMainSprite(pi);
        let txt = this.createDesc(decorationId, pi);

        this.add([stand, main, ...txt]);
    }

    private createStandSprite(decorationId: string): Sprite {
        let key = 'stand_1';
        if (decorationId === 'pack_2') {
            key = 'stand_2';
        }
        return SpriteUtils.addSprite(this.scene, 0, 0, Keys.ATLAS_BANK_2, key);
    }

    private createMainSprite(pi: PurchaseItem): Container | Sprite {
        let yD = -50;
        switch (pi.itemType) {
            case ItemType.HERO:
                if (pi.creature) {
                    let res = new CreaturePanel(this.scene, 0, -66, pi.creature, () => {}, true);
                    res.setHealthVisible();
                    return res;
                } else {
                    return SpriteUtils.addSprite(this.scene, 0, -66, AtlasUtils.getCreatureBattleAtlas(pi.id), pi.id);
                }
            case ItemType.AZURE_CHEST:
                return SpriteUtils.addSprite(this.scene, 0, -31, Keys.CHEST_AZURE_CLOSED, undefined)
                    .setScale(0.47);
            case ItemType.LAVA_CHEST:
                return SpriteUtils.addSprite(this.scene, 0, -31, Keys.CHEST_LAVA_CLOSED, undefined)
                    .setScale(0.47);
            case ItemType.SILVER_CHEST:
                return SpriteUtils.addSprite(this.scene, 0, -31, Keys.CHEST_SILVER_CLOSED, undefined)
                    .setScale(0.47);
            case ItemType.GOLD_CHEST:
                return SpriteUtils.addSprite(this.scene, 0, -31, Keys.CHEST_GOLDEN_CLOSED, undefined)
                    .setScale(0.47);
            case ItemType.AMBER_CHEST:
                return SpriteUtils.addSprite(this.scene, 0, -31, Keys.CHEST_AMBER_CLOSED, undefined)
                    .setScale(0.47);
            case ItemType.JADE_CHEST:
                return SpriteUtils.addSprite(this.scene, 0, -31, Keys.CHEST_JADE_CLOSED, undefined)
                    .setScale(0.47);
            case ItemType.ELEPHANT:
                return SpriteUtils.addSprite(this.scene, 0, -44, Keys.CARD_ELEPHANT, undefined)
                    .setScale(0.47);
            case ItemType.GEMS:
                if (pi.id) {
                    return SpriteUtils.addSprite(this.scene, 3, -36, pi.id, undefined);
                } else {
                    return SpriteUtils.addSprite(this.scene, 3, -36, Keys.ATLAS_BANK, 'gems-1');
                }
            case ItemType.GEMS_BONUS:
            case ItemType.GEMS_BONUS_PROGRAM:
                return SpriteUtils.addSprite(this.scene, 0, yD, Keys.ATLAS_BANK, 'gems-1');
            case ItemType.ATTACK_COMBATIVE:
                return SpriteUtils.addSprite(this.scene, 0, -36, Keys.ATL_PACK_SKILLS, 'attack');
            case ItemType.DEFENCE_COMBATIVE:
                return SpriteUtils.addSprite(this.scene, 0, -36, Keys.ATL_PACK_SKILLS, 'defence');
            case ItemType.HEALTH:
            case ItemType.HEALTH_COMBATIVE:
                return SpriteUtils.addSprite(this.scene, 0, -36, Keys.ATL_PACK_SKILLS, 'health');
            case ItemType.MYTH_DISCOUNT:
                return SpriteUtils.addSprite(this.scene, 0, yD, Keys.ATLAS_BANK_2, 'discount');
            case ItemType.TICKETS:
                return SpriteUtils.addSprite(this.scene, 0, yD, Keys.IMG_TICKET_BIG, undefined);
        }
        return SpriteUtils.addSprite(this.scene, 0, yD, Keys.ATLAS_BANK_2, 'discount');
    }

    private createDesc(decorationId: string, pi: PurchaseItem): (Text | Image)[] {
        let self = this;

        switch (pi.itemType) {
            case ItemType.HERO:
                let titleText = TextUtils.addTextAdvanced(this.scene, pi.creature.name, 0, 34,
                    '#fbd777', pi.creature.name.length > 10 ? 16 : 18, 'normal', 450, 'center', 2, Fonts.BENGALY_NORMAL)
                    .setOrigin(0.5, 0.5);
                titleText.setStroke('#8c4f19', 4);

                let desc = TextUtils.addTextAdvanced(this.scene, Tr('klass') + CreatureUtils.getClassName(pi.creature.warriorClass),
                    0, 54, '#b3b7b6', 11, 'normal', 450, 'center', 2, Fonts.MYRIADPRO_REGULAR)
                    .setOrigin(0.5, 0.5);
                desc.setStroke('#2a2a2a', 2);

                let icon = SpriteUtils.addImage(this.scene!, desc.getTopLeft().x! / CU.S - 4, 61, Keys.ATLAS_UI, Keys.UI_STAT_MINE_CIRCLE)
                    .setOrigin(0.8).setScale(0.8);
                icon.setInteractive().on('pointerup', function(pointer: any) {
                    OnceCaller.doOnce('heroManual', () => {
                        self.heroDescCallback(pi.creature.imageId);
                    });
                });

                return [titleText, desc, icon];
            case ItemType.SILVER_CHEST:
            case ItemType.AZURE_CHEST:
            case ItemType.LAVA_CHEST:
            case ItemType.GOLD_CHEST:
            case ItemType.AMBER_CHEST:
            case ItemType.JADE_CHEST:
            case ItemType.ELEPHANT:
                let text1 = String(pi.count) + SpaceTr('sst');
                let titleText1 = TextUtils.addTextAdvanced(this.scene, text1, 0, 25,
                    '#fbd777', 18, 'normal', 450, 'center', 2, Fonts.BENGALY_NORMAL)
                    .setOrigin(0.5, 0.5);
                titleText1.setStroke('#8c4f19', 4);

                return [titleText1];
            case ItemType.GEMS:
                break;
            case ItemType.GEMS_BONUS:
            case ItemType.GEMS_BONUS_PROGRAM:
                break;
            case ItemType.ATTACK_COMBATIVE:
            case ItemType.DEFENCE_COMBATIVE:
            case ItemType.HEALTH_COMBATIVE:
            case ItemType.HEALTH:
                let text3 = '+' + String(pi.count) + '% ' + Tr('cmb');
                let titleText3 = TextUtils.addTextAdvanced(this.scene, text3, 0, 25,
                    '#fbd777', 12, 'normal', 130, 'center', 2, Fonts.BENGALY_NORMAL)
                    .setOrigin(0.5, 0.5);
                titleText3.setStroke('#8c4f19', 4);

                return [titleText3];
            case ItemType.MYTH_DISCOUNT:
                let text2 = '-' + String(pi.count) + '%' + Tr('disc.mth');
                let titleText2 = TextUtils.addTextAdvanced(this.scene, text2, 0, 25,
                    '#fbd777', 15, 'normal', 100, 'center', 2, Fonts.BENGALY_NORMAL)
                    .setOrigin(0.5, 0.5);
                titleText2.setStroke('#8c4f19', 4);

                return [titleText2];
            case ItemType.TICKETS:
                break;

        }
        let text = pi.count < 10000 ? String(pi.count) : TextUtils.formatMoney(pi.count);
        let titleText = TextUtils.addTextAdvanced(this.scene, text, 0, 25,
            '#fbd777', 18, 'normal', 450, 'center', 2, Fonts.BENGALY_NORMAL)
            .setOrigin(0.5, 0.5);
        titleText.setStroke('#8c4f19', 4);

        return [titleText];
    }

}
